import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency } from "@/lib/formatters";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function RevenueTrendChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-daily-trend", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("daily-trend", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Revenue Trend" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-revenue-trend">No revenue data for this period</div>
      ) : (
        <div className="px-4 pb-3 h-full" style={{ background: "#F7F9FC" }} data-testid="chart-revenue-trend">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DCE3ED" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={(v: string) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={formatCurrency} width={60} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #DCE3ED" }} formatter={(v: number) => [formatCurrency(v), "Revenue"]} labelFormatter={(l: string) => l} />
              <Area type="monotone" dataKey="purchaseValue" stroke="#6366F1" strokeWidth={2} fill="#E0E7FF" fillOpacity={0.15} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </DashboardWidget>
  );
}
